import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { apiUrl } from '../utils/api';

const API_URL = apiUrl('/api/usuarios/');

const INITIAL_FORM = {
  username: '',
  email: '',
  first_name: '',
  last_name: '',
  role: 'tecnico',
  password: '',
};

const ListaUsuarios = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM);
  const [showPassword, setShowPassword] = useState(false);
  const token = localStorage.getItem('token');

  const headers = useMemo(
    () => ({
      Authorization: `Token ${token}`,
    }),
    [token]
  );

  const cargarUsuarios = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const res = await axios.get(API_URL, { headers });
      setUsuarios(res.data);
    } catch (err) {
      console.error(err);
      setError('No tienes permiso para ver esta sección.');
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    cargarUsuarios();
  }, [cargarUsuarios]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const resetForm = () => {
    setForm(INITIAL_FORM);
    setShowPassword(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);

    try {
      await axios.post(
        API_URL,
        {
          ...form,
          email: form.email.trim().toLowerCase(),
        },
        { headers }
      );

      toast.success('Usuario creado correctamente');
      resetForm();
      await cargarUsuarios();
    } catch (err) {
      console.error(err);
      const apiErrors = err.response?.data;
      const mensaje =
        typeof apiErrors === 'string'
          ? apiErrors
          : Object.values(apiErrors || {})
              .flat()
              .join(' ') || 'No se pudo crear el usuario.';
      toast.error(mensaje);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="container page-shell">
      <div className="page-header">
        <div>
          <span className="section-kicker">Administración</span>
          <h2 className="page-title">Usuarios</h2>
          <p className="page-subtitle">
            Solo los administradores pueden crear nuevas cuentas y revisar el listado actual.
          </p>
        </div>
        <img src="/logo-inta.png" alt="Logo INTA" className="page-header-logo" />
      </div>

      <div className="row g-4">
        <div className="col-lg-4">
          <div className="surface-card h-100">
            <div className="mb-4">
              <h3 className="form-title mb-2">Crear usuario</h3>
              <p className="page-subtitle mb-0">Completa los datos básicos y asigna el rol inicial.</p>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label className="form-label">Usuario</label>
                <input
                  type="text"
                  name="username"
                  className="form-control"
                  value={form.username}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  name="email"
                  className="form-control"
                  value={form.email}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Nombre</label>
                <input
                  type="text"
                  name="first_name"
                  className="form-control"
                  value={form.first_name}
                  onChange={handleChange}
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Apellido</label>
                <input
                  type="text"
                  name="last_name"
                  className="form-control"
                  value={form.last_name}
                  onChange={handleChange}
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Rol</label>
                <select name="role" className="form-select" value={form.role} onChange={handleChange}>
                  <option value="admin">Administrador</option>
                  <option value="supervisor">Supervisor</option>
                  <option value="tecnico">Técnico</option>
                </select>
              </div>

              <div className="mb-4">
                <label className="form-label">Contraseña temporal</label>
                <div className="input-group">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    className="form-control"
                    value={form.password}
                    onChange={handleChange}
                    minLength={8}
                    required
                  />
                  <button
                    type="button"
                    className="btn btn-outline-brand"
                    onClick={() => setShowPassword((current) => !current)}
                    aria-label={showPassword ? 'Ocultar contrasena' : 'Mostrar contrasena'}
                    aria-pressed={showPassword}
                  >
                    {showPassword ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
              </div>

              <button type="submit" className="btn btn-brand w-100" disabled={saving}>
                {saving ? 'Creando...' : 'Crear usuario'}
              </button>
            </form>
          </div>
        </div>

        <div className="col-lg-8">
          <div className="surface-card h-100">
            <div className="d-flex justify-content-between align-items-start gap-3 mb-4">
              <div>
                <h3 className="form-title mb-2">Usuarios registrados</h3>
                <p className="page-subtitle mb-0">Listado de cuentas creadas en el sistema.</p>
              </div>
              <span className="protocol-chip">{usuarios.length} usuarios</span>
            </div>

            {loading ? (
              <div className="dashboard-empty">Cargando usuarios...</div>
            ) : error ? (
              <div className="alert subtle-alert mb-0">{error}</div>
            ) : (
              <div className="table-responsive">
                <table className="table align-middle">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Usuario</th>
                      <th>Email</th>
                      <th>Nombre</th>
                      <th>Apellido</th>
                      <th>Rol</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usuarios.map((user) => (
                      <tr key={user.id}>
                        <td>{user.id}</td>
                        <td>{user.username}</td>
                        <td>{user.email}</td>
                        <td>{user.first_name || '-'}</td>
                        <td>{user.last_name || '-'}</td>
                        <td>{user.role}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ListaUsuarios;
